import { useCallback, useMemo, useState, type ReactNode } from "react";
import type { CellContext } from "@tanstack/react-table";
import { View, ViewDensity, ViewType } from "@common/types/views/views";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import type { ViewConfig } from "@lib/views";
import { buildFlatItems, plainFlatItemOptions } from "./PickerPlayground.lib";
import styles from "./PickerPlayground.module.scss";

/** Ephemeral view for the sandbox tables; the playground never persists layout. */
const buildHostView = (columnIds: readonly string[]): View => ({
    type: ViewType.DESIGN_TOKENS,
    columns: columnIds.map((id) => ({ id })),
    filters: {},
    options: { selection: false, bulkActions: false, density: ViewDensity.SM },
});

const TextCell = <TRow extends { id: string }>({
    getValue,
}: CellContext<ViewTableFeatures, TRow, string>) => (
    <ViewTableCell>{getValue()}</ViewTableCell>
);

export interface PickerCellHostRow {
    id: string;
    label: string;
    /** The Picker root (or any content) the "Cell" column hosts for this row. */
    cell: ReactNode;
}

type HostColumnId = "label" | "cell";

/**
 * A real `ViewTable` whose second column hosts arbitrary content per row: the
 * matrix and the stage put a `Picker.Select` cell / cellInput root in it so
 * the chromeless triggers sit in the chrome they were designed for (row
 * hover, `captureClicks`, the table's own padding) instead of a fake box.
 */
export const PickerCellTableHost = ({
    rows,
    cellHeader = "Picker cell",
}: {
    rows: PickerCellHostRow[];
    cellHeader?: string;
}) => {
    const view = useMemo(() => buildHostView(["label", "cell"]), []);
    const config = useMemo<ViewConfig<PickerCellHostRow, HostColumnId, never>>(
        () => ({
            columns: {
                label: {
                    accessorKey: "label",
                    name: "Variant",
                    header: "Variant",
                    cell: TextCell,
                    minSize: 160,
                    maxSize: 200,
                    immutable: true,
                },
                cell: {
                    accessorKey: "id",
                    name: cellHeader,
                    header: cellHeader,
                    cell: ({ row }) => (
                        <ViewTableCell
                            captureClicks
                            className={styles.hostCell}
                        >
                            {row.original.cell}
                        </ViewTableCell>
                    ),
                    minSize: 220,
                },
            },
        }),
        [cellHeader],
    );

    return (
        <div className={styles.tableHost}>
            <ViewTable data={rows} view={view} config={config} rowBorders />
        </div>
    );
};

interface LedgerRow {
    id: string;
    name: string;
    account: string | null;
    category: string | null;
}

type LedgerColumnId = "name" | "account" | "category";

const ledgerRows: LedgerRow[] = [
    { id: "row-1", name: "Office rent", account: null, category: null },
    { id: "row-2", name: "Cloud hosting", account: null, category: null },
    { id: "row-3", name: "Team lunch", account: null, category: null },
];

/**
 * Three rows, three columns: a text column, a `trigger="cell"` column and a
 * `trigger="cellInput"` column, each cell bound to its row's data the way
 * product tables bind theirs.
 */
export const PickerViewTableExample = () => {
    const items = useMemo(() => buildFlatItems(plainFlatItemOptions), []);
    const [rows, setRows] = useState(ledgerRows);
    const updateRow = useCallback(
        (id: string, patch: Partial<Pick<LedgerRow, "account" | "category">>) =>
            setRows((current) =>
                current.map((row) =>
                    row.id === id ? { ...row, ...patch } : row,
                ),
            ),
        [],
    );
    const view = useMemo(
        () => buildHostView(["name", "account", "category"]),
        [],
    );
    const config = useMemo<ViewConfig<LedgerRow, LedgerColumnId, never>>(
        () => ({
            columns: {
                name: {
                    accessorKey: "name",
                    name: "Description",
                    header: "Description",
                    cell: TextCell,
                    minSize: 160,
                    maxSize: 220,
                    immutable: true,
                },
                account: {
                    accessorKey: "account",
                    name: "Account (cell)",
                    header: "Account (cell)",
                    cell: ({ row }) => (
                        <ViewTableCell
                            captureClicks
                            className={styles.hostCell}
                        >
                            <PickerAccountCell
                                items={items}
                                value={row.original.account}
                                onChange={(account) =>
                                    updateRow(row.original.id, { account })
                                }
                            />
                        </ViewTableCell>
                    ),
                    minSize: 200,
                },
                category: {
                    accessorKey: "category",
                    name: "Category (cellInput)",
                    header: "Category (cellInput)",
                    cell: ({ row }) => (
                        <ViewTableCell
                            captureClicks
                            className={styles.hostCell}
                        >
                            <Picker.Select
                                items={items}
                                trigger="cellInput"
                                placeholder="Type a category"
                                value={row.original.category}
                                onValueChange={(category) =>
                                    updateRow(row.original.id, { category })
                                }
                            />
                        </ViewTableCell>
                    ),
                    minSize: 200,
                },
            },
        }),
        [items, updateRow],
    );

    return (
        <div className={styles.tableHost}>
            <ViewTable data={rows} view={view} config={config} rowBorders />
        </div>
    );
};

const PickerAccountCell = ({
    items,
    value,
    onChange,
}: {
    items: Array<PickerItemData<string>>;
    value: string | null;
    onChange: (value: string | null) => void;
}) => (
    <Picker.Select
        items={items}
        trigger="cell"
        placeholder="Add account"
        value={value}
        onValueChange={onChange}
    />
);
